import {
  WorkflowRunKind,
  type WorkflowRunHistoryDetail,
  type WorkflowRunNodeDetail,
  type WorkflowRunNodeStatus,
} from '@/apis/logiblocksAPI';
import type { RunGraphSource } from './runHistory/runGraphSnapshotCache';
import {
  RUN_COMPLETED_COLOR,
  RUN_FAILED_COLOR,
  RUN_SKIPPED_EDGE_COLOR,
} from './styles';

// One canvas node can have several status rows: a loop reuses its child node
// ids across iterations. When they disagree, the "worst" outcome is used for
// display purposes. (So: a single failed loop iteration marks the node failed.)
const STATUS_PRECEDENCE: Record<WorkflowRunNodeStatus, number> = {
  FAILED: 3,
  COMPLETED: 2,
  RUNNING: 1,
  SKIPPED_CHILDREN: 0,
};

/**
 * Collapses a run's per-node status rows to one status per canvas node. Nodes
 * that were never reached are absent from the map.
 */
export function buildRunNodeStatusMap(
  nodes: WorkflowRunNodeDetail[]
): Map<string, WorkflowRunNodeStatus> {
  const statusByNodeId = new Map<string, WorkflowRunNodeStatus>();
  for (const node of nodes) {
    const existing = statusByNodeId.get(node.node_id);
    if (
      existing == null ||
      STATUS_PRECEDENCE[node.status] > STATUS_PRECEDENCE[existing]
    ) {
      statusByNodeId.set(node.node_id, node.status);
    }
  }
  return statusByNodeId;
}

/**
 * A node ring is the coloured inset outline around a node card. These are the
 * variants it takes for the selected run ('dimmed' doesn't draw a ring -it fades the
 * card instead).
 */
export type NodeRingVariant = 'executed' | 'failed' | 'dimmed';

/**
 * How a node should read on the canvas for the selected run: a node that ran
 * (completed or still running) reads executed, a failed node reads failed, and
 * a skipped or never-reached node is dimmed.
 */
export function getNodeRingVariant(
  status: WorkflowRunNodeStatus | undefined
): NodeRingVariant {
  switch (status) {
    case 'COMPLETED':
    case 'RUNNING':
      return 'executed';
    case 'FAILED':
      return 'failed';
    case 'SKIPPED_CHILDREN':
    case undefined:
      return 'dimmed';
    default:
      console.warn(`Unexpected node status: ${status}`);
      return 'dimmed';
  }
}

/**
 * The colour an edge is drawn in for the selected run. Edges take the treatment
 * of the node they point at.
 */
export function getRunEdgeColor(variant: NodeRingVariant): string {
  switch (variant) {
    case 'executed':
      return RUN_COMPLETED_COLOR;
    case 'failed':
      return RUN_FAILED_COLOR;
    case 'dimmed':
      return RUN_SKIPPED_EDGE_COLOR;
    default:
      console.warn(`Unexpected node ring variant: ${variant}`);
      return RUN_SKIPPED_EDGE_COLOR;
  }
}

/**
 * How the graph a run executed relates to the live workflow.
 *
 * 'current': the run ran the workflow as it stands now.
 * 'older': the run ran an earlier version, or the workflow has since been
 *   unpublished and edited, so its snapshot differs from the live workflow.
 * 'missing': the run predates version tracking, so there is no snapshot of the
 *   graph that ran and nothing accurate to show.
 * 'test': a test run, which ran the graph stored with it rather than a
 *   published version.
 */
export type RunGraphStatus = 'current' | 'older' | 'missing' | 'test';

/**
 * Classifies the graph a run executed against the live workflow, which decides
 * whether the run's snapshot can be shown and whether to note it is not current.
 */
export function getRunGraphStatus(
  detail: WorkflowRunHistoryDetail,
  isDraft: boolean
): RunGraphStatus {
  // A test run has no version. Its graph is stored with the run.
  if (detail.kind === WorkflowRunKind.TEST) {
    return 'test';
  }
  // A run with no recorded version has no snapshot of what ran.
  if (detail.workflow_version_number === null) {
    return 'missing';
  }
  // A published workflow cannot be edited, so a draft that was published before
  // has since been unpublished to edit and no longer matches what ran.
  if (
    isDraft ||
    detail.workflow_version_number !== detail.current_version_number
  ) {
    return 'older';
  }
  return 'current';
}

/**
 * Returns where to load the graph a run executed: the stored graph for a test
 * run, the published version for a live run, or null for a live run that
 * predates version tracking.
 */
export function getRunGraphSource(
  detail: WorkflowRunHistoryDetail
): RunGraphSource | null {
  switch (detail.kind) {
    case WorkflowRunKind.TEST:
      return { kind: WorkflowRunKind.TEST, runId: detail.id };
    case WorkflowRunKind.LIVE:
      if (detail.workflow_version_number === null) {
        return null;
      }
      return {
        kind: WorkflowRunKind.LIVE,
        versionNumber: detail.workflow_version_number,
      };
  }
}
